// screens/create-product-mgt/AllocateAddon.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Table, Card, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { TrashFill } from 'react-bootstrap-icons';

const AllocateAddon = ({ data, onNext, onBack, updateData, currentStep, isSubmitting, setIsSubmitting, setErrors }) => {
    
    const accessToken = localStorage.getItem('authToken');
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            minWidth: "200px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#80bdff" : "#ced4da"
            }
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d"
        })
    };

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [addonsList, setAddonsList] = useState([]);
    const [availableAddons, setAvailableAddons] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [showAddMoreModal, setShowAddMoreModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    const [allocatedAddons, setAllocatedAddons] = useState([]);
    const [currentAddon, setCurrentAddon] = useState({
        selectedAddon: null,
        selectedExcludedValues: [],
        selectedExcludedOutlets: [],
        allocatedValues: [],
        addonValues: []
    });

    const animatedComponents = makeAnimated();
    const productData = data.product || {};
    const productId = productData._id;

    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({
        _id: null,
        product: null,
        addon: null,
        excludedValues: [],
        excludedOutlets: [],
        groupStatus: "ACTIVE",
        values: []
    });
    const [isEditBtnLoading, setIsEditBtnLoading] = useState(false);
    const [selectedExcludedOption, setSelectedExcludedOption] = useState(null);

    useEffect(() => {
        getAddonsList();
        getOutlets();
        if (data.addons?.allocatedAddons) {
            setAllocatedAddons(data.addons.allocatedAddons);
        }
    }, []);

    useEffect(() => {
        if (addonsList.length > 0) {
            const allocatedIds = allocatedAddons.map(a => a.addonId);
            const filtered = addonsList.filter(addon => !allocatedIds.includes(addon._id));
            setAvailableAddons(filtered);
        }
    }, [addonsList, allocatedAddons]);

    useEffect(() => {
        if (currentAddon.selectedAddon) {
            const addon = addonsList.find(a => a._id === currentAddon.selectedAddon.value);
            if (addon && addon.addonValues) {
                const initialAllocatedValues = addon.addonValues.map(av => ({
                    _id: av._id,
                    value: { value: av._id, label: `${av.sequenceNumber}. ${av.value}` },
                    price: '',
                    subLabel: '',
                    isDefault: false,
                    excludedOutlets: []
                }));
                
                setCurrentAddon(prev => ({
                    ...prev,
                    addonValues: addon.addonValues,
                    allocatedValues: initialAllocatedValues,
                    selectedExcludedValues: [],
                    selectedExcludedOutlets: []
                }));
            }
        }
    }, [currentAddon.selectedAddon]);

    const getAddonsList = async () => {
        setIsLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("addons?page=1&limit=100", { headers });
            if (response?.data?.status) {
                setAddonsList(response?.data?.data?.addons || []);
            }
        } catch (error) {
            toast.error("Failed to fetch addons", {
                position: "top-right",
                autoClose: 3000,
                theme: "light"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true) {
                const filteredOutlets = response.data.data.outlets.filter(
                    (act) => act.status === "ACTIVE"
                );
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            toast.error("Failed to load outlets", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const handleAddonChange = (selectedOption) => {
        setCurrentAddon(prev => ({
            ...prev,
            selectedAddon: selectedOption,
            selectedExcludedValues: [],
            selectedExcludedOutlets: []
        }));
    };

    const handleValuePriceChange = (valueId, price) => {
        setCurrentAddon(prev => ({
            ...prev,
            allocatedValues: prev.allocatedValues.map(val =>
                val.value.value === valueId
                    ? { ...val, price: price }
                    : val
            )
        }));
    };

    const handleValueSubLabelChange = (valueId, subLabel) => {
        setCurrentAddon(prev => ({
            ...prev,
            allocatedValues: prev.allocatedValues.map(val =>
                val.value.value === valueId
                    ? { ...val, subLabel: subLabel }
                    : val
            )
        }));
    };

    const handleDefaultChange = (valueId) => {
        setCurrentAddon(prev => ({
            ...prev,
            allocatedValues: prev.allocatedValues.map(val => ({
                ...val,
                isDefault: val.value.value === valueId
            }))
        }));
    };

    const handleValueExcludedOutlets = (valueId, selectedOutlets) => {
        setCurrentAddon(prev => ({
            ...prev,
            allocatedValues: prev.allocatedValues.map(val =>
                val.value.value === valueId
                    ? { ...val, excludedOutlets: selectedOutlets || [] }
                    : val
            )
        }));
    };

    const handleExcludedValueChange = (selectedOptions) => {
        setCurrentAddon(prev => ({
            ...prev,
            selectedExcludedValues: selectedOptions || []
        }));
    };

    const handleExcludedOutletChange = (selectedOptions) => {
        setCurrentAddon(prev => ({
            ...prev,
            selectedExcludedOutlets: selectedOptions || []
        }));
    };

    const validateCurrentAddon = () => {
        if (!currentAddon.selectedAddon) {
            toast.error('Please select an addon group', { position: 'top-right' });
            return false;
        }

        const excludedValueIds = currentAddon.selectedExcludedValues.map(v => v.value);
        const filteredAllocatedValues = currentAddon.allocatedValues.filter(val =>
            !excludedValueIds.includes(val.value.value)
        );

        const hasPricedValues = filteredAllocatedValues.some(val => val.price && parseFloat(val.price) > 0);
        if (!hasPricedValues) {
            toast.error('Please set prices for at least one addon value', { position: 'top-right' });
            return false;
        }

        return true;
    };

    const saveCurrentAddon = async () => {
        if (!validateCurrentAddon()) {
            return;
        }

        const excludedValueIds = currentAddon.selectedExcludedValues.map(v => v.value);
        const filteredAllocatedValues = currentAddon.allocatedValues.filter(val =>
            !excludedValueIds.includes(val.value.value)
        );

        const payload = {
            product: productData._id,
            addon: currentAddon.selectedAddon.value,
            excludedOutlets: currentAddon.selectedExcludedOutlets.map(out => out.value),
            excludedValues: excludedValueIds,
            status: "ACTIVE",
            values: filteredAllocatedValues.map(val => ({
                value: val.value.value,
                price: parseFloat(val.price) || 0,
                subLabel: val.subLabel || '',
                status: "ACTIVE",
                isDefault: val.isDefault || false,
                excludedOutlets: val.excludedOutlets?.map(out => out.value) || []
            }))
        };

        // Remove empty arrays
        Object.keys(payload).forEach(key => {
            if (Array.isArray(payload[key]) && payload[key].length === 0) {
                delete payload[key];
            }
        });

        payload.values = payload.values.map(val => {
            if (val.excludedOutlets?.length === 0) delete val.excludedOutlets;
            return val;
        });

        setIsSaving(true);
        setIsSubmitting(true);

        try {
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.post('addon-product-allocation', payload, { headers });
            console.log("Response of Proceed Button :", response);
            
            if (response?.data?.status) {
                // Add to allocated addons list with full details
                const newAllocatedAddon = {
                    _id: response.data.data?._id,
                    allocationId: response.data.data?._id,
                    addonId: currentAddon.selectedAddon.value,
                    addonName: currentAddon.selectedAddon.label,
                    payload: payload,
                    allocatedValues: filteredAllocatedValues,
                    excludedValues: currentAddon.selectedExcludedValues,
                    excludedOutlets: currentAddon.selectedExcludedOutlets
                };

                setAllocatedAddons(prev => [...prev, newAllocatedAddon]);
                
                // Clear current addon form
                resetCurrentAddon();
                
                toast.success('Addon allocated successfully!', { position: 'top-right' });
                
                // Ask if user wants to add more
                setShowAddMoreModal(true);
                
            } else {
                toast.error(response?.data?.error || 'Failed to allocate addon', { position: 'top-right' });
            }
        } catch (error) {
            console.error('Error allocating addon:', error);

            let errorMessage = 'Failed to allocate addon';
            if (error.response) {
                if (error.response.status === 406) {
                    errorMessage = error.response.data.error?.message || error.response.data.error;
                } else if (error.response.status === 412) {
                    errorMessage = error.response.data.data?.message;
                } else if (error.response.status === 400) {
                    errorMessage = error.response.data.error;
                } else if (error.response.status === 409) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 500) {
                    errorMessage = error.response.data.message || error.response.data.error;
                }
            }

            toast.error(errorMessage, { position: "top-right", autoClose: 3000, theme: "colored" });
            setErrors([errorMessage]);
        } finally {
            setIsSaving(false);
            setIsSubmitting(false);
        }
    };

    const resetCurrentAddon = () => {
        setCurrentAddon({
            selectedAddon: null,
            selectedExcludedValues: [],
            selectedExcludedOutlets: [],
            allocatedValues: [],
            addonValues: []
        });
    };

    const handleAddAnother = () => {
        setShowAddMoreModal(false);
    };

    const handleProceedToNext = () => {
        setShowAddMoreModal(false);
        updateData('addons', {
            allocatedAddons: allocatedAddons,
            totalAddons: allocatedAddons.length,
            isAllocated: allocatedAddons.length > 0,
            skipped: false
        });
        onNext();
    };

    const handleSkipAddons = () => {
        setShowConfirmModal(true);
    };

    const confirmSkipAddons = () => {
        setShowConfirmModal(false);
        updateData('addons', {
            allocatedAddons: [],
            totalAddons: 0,
            isAllocated: false,
            skipped: true
        });
        toast.info('Add-ons step skipped. You can add addons later if needed.', {
            position: "top-right",
            autoClose: 3000,
            theme: "light"
        });
        onNext();
    };

    const removeAllocatedAddon = async (index) => {
        const addonToRemove = allocatedAddons[index];
        
        if (!addonToRemove.allocationId) {
            toast.error('Cannot delete: Missing allocation ID', { position: 'top-right' });
            return;
        }

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.delete(`addon-product-allocation?_id=${addonToRemove.allocationId}`, { headers });
            
            if (response?.data?.status === true) {
                setAllocatedAddons(prev => prev.filter((_, i) => i !== index));
                toast.success('Addon removed successfully', { position: 'top-right' });
            } else {
                toast.error('Failed to remove addon', { position: 'top-right' });
            }
        } catch (error) {
            console.error('Error removing addon:', error);
            toast.error('Failed to remove addon', { position: 'top-right' });
        }
    };

    const updateAddon = (index, allocation) => {
        console.log("Edit Allocate Modal Data :", allocation);
        
        const addonOption = { 
            value: allocation?.addonId || allocation?.payload?.addon, 
            label: allocation?.addonName 
        };

        // Get the complete addon details
        const completeAddon = addonsList.find(a => a._id === addonOption.value);
        
        // Map allocated values
        const mappedValues = allocation?.allocatedValues?.map(val => ({
            _id: val?._id,
            value: {
                value: val?.value?.value,
                label: `${val?.value?.label || val?.value?.value}`,
                _id: val?.value?.value
            },
            price: val?.price?.toString() || '',
            subLabel: val?.subLabel || '',
            excludedOutlets: val?.excludedOutlets?.map((out) => ({
                value: out.value || out._id,
                label: out.label || out.name
            })) || [],
            isDefault: val?.isDefault || false
        }));

        // Map excluded values
        const excludedValueIds = allocation?.payload?.excludedValues || [];
        const allAddonValues = completeAddon?.addonValues || [];
        
        // Create excluded values options
        const excludedValues = allAddonValues
            .filter(av => excludedValueIds.includes(av._id))
            .map(av => ({
                value: av._id,
                label: `${av.sequenceNumber}. ${av.value}`
            }));

        // Map excluded outlets
        const excludedOutlets = allocation?.excludedOutlets?.map(out => ({
            value: out.value || out._id,
            label: out.label || out.name
        })) || [];

        setEditFormData({
            _id: allocation?.allocationId || allocation?._id,
            product: { value: productData._id, label: productData.name },
            addon: addonOption,
            excludedValues: excludedValues,
            excludedOutlets: excludedOutlets,
            groupStatus: "ACTIVE",
            values: mappedValues
        });
        setShowEditModal(true);
    };

    const handleEditValueChange = (index, field, value) => {
        const updatedValues = [...editFormData.values];
        updatedValues[index] = { ...updatedValues[index], [field]: value };
        setEditFormData({ ...editFormData, values: updatedValues });
    };

    const handleEditRemoveValue = (index) => {
        const removedRow = editFormData.values[index];
        const removedValue = removedRow?.value;

        if (!removedValue) return;

        const valueId = removedValue._id || removedValue.value;
        const label = removedValue.label || removedValue.value;

        if (!valueId) return;

        const updatedExcluded = [...editFormData.excludedValues];
        const alreadyExists = updatedExcluded.find(opt => opt.value === valueId);

        if (!alreadyExists) {
            updatedExcluded.push({
                value: valueId,
                label: label
            });
        }

        const updatedValues = editFormData.values.filter((_, idx) => idx !== index);

        setEditFormData({
            ...editFormData,
            values: updatedValues,
            excludedValues: updatedExcluded
        });
    };

    const handleEditRemoveExcludedValue = (removedOption) => {
        const updatedExcluded = editFormData.excludedValues.filter(
            (opt) => opt.value !== removedOption.value
        );
        const existsInAllocated = editFormData.values.find(
            (row) => row.value && row.value.value === removedOption.value
        );
        
        let updatedValues = editFormData.values;
        if (!existsInAllocated) {
            updatedValues = [
                ...editFormData.values,
                {
                    value: removedOption,
                    price: '',
                    subLabel: '',
                    excludedOutlets: [],
                    isDefault: false
                }
            ];
        }
        setEditFormData({ ...editFormData, excludedValues: updatedExcluded, values: updatedValues });
    };

    const handleEditMainSelectChange = (selected, index, isGroup = false) => {
        if (isGroup) {
            setEditFormData((prev) => ({ ...prev, excludedOutlets: selected || [] }));
        } else {
            const updatedValues = [...editFormData.values];
            updatedValues[index] = {
                ...updatedValues[index],
                excludedOutlets: selected || []
            };
            setEditFormData({ ...editFormData, values: updatedValues });
        }
    };

    const handleEditAllocationSubmit = async (e) => {
        e.preventDefault();
        setIsEditBtnLoading(true);

        try {
            const payload = {
                _id: editFormData._id,
                product: editFormData.product.value,
                addon: editFormData.addon.value,
                excludedOutlets: editFormData.excludedOutlets.map(opt => opt.value),
                excludedValues: editFormData.excludedValues.map(opt => opt.value),
                status: editFormData.groupStatus,
                values: editFormData.values.map(val => ({
                    _id: val?._id,
                    value: val.value.value,
                    price: parseFloat(val.price) || 0,
                    subLabel: val.subLabel || '',
                    status: "ACTIVE",
                    excludedOutlets: val.excludedOutlets?.map(opt => opt.value) || [],
                    isDefault: val.isDefault || false
                }))
            };

            // Remove empty arrays
            Object.keys(payload).forEach(key => {
                if (Array.isArray(payload[key]) && payload[key].length === 0) {
                    delete payload[key];
                }
            });

            payload.values = payload.values.map(val => {
                if (val.excludedOutlets?.length === 0) delete val.excludedOutlets;
                return val;
            });

            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.put('addon-product-allocation', payload, { headers });

            if (response?.data?.status) {
                // Update the allocated addon in state
                const updatedAllocation = {
                    _id: response.data.data?.dbResponse?._id || editFormData._id,
                    allocationId: response.data.data?.dbResponse?._id || editFormData._id,
                    addonId: editFormData.addon.value,
                    addonName: editFormData.addon.label,
                    payload: payload,
                    allocatedValues: editFormData.values,
                    excludedValues: editFormData.excludedValues,
                    excludedOutlets: editFormData.excludedOutlets
                };

                // Find and update the allocation
                setAllocatedAddons(prev => 
                    prev.map(alloc => 
                        alloc.allocationId === editFormData._id ? updatedAllocation : alloc
                    )
                );

                toast.success('Addon updated successfully!', { 
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored'
                });
                setShowEditModal(false);
            } else {
                toast.error(response?.data?.error || 'Failed to update addon', { 
                    position: 'top-right',
                    autoClose: 3000,
                    theme: 'colored'
                });
            }
        } catch (error) {
            console.error('Error updating addon:', error);
            
            let errorMessage = 'Failed to update addon';
            if (error.response) {
                if (error.response.status === 406) {
                    errorMessage = error.response.data.error?.message || error.response.data.error;
                } else if (error.response.status === 412) {
                    errorMessage = error.response.data.data?.message;
                } else if (error.response.status === 400) {
                    errorMessage = error.response.data.error;
                } else if (error.response.status === 409) {
                    errorMessage = error.response.data.data?.message || error.response.data.error;
                } else if (error.response.status === 500) {
                    errorMessage = error.response.data.message || error.response.data.error;
                }
            }

            toast.error(errorMessage, { 
                position: "top-right", 
                autoClose: 3000, 
                theme: "colored" 
            });
        } finally {
            setIsEditBtnLoading(false);
        }
    };

    const outletOptions = outlets.map(outlet => ({
        value: outlet._id,
        label: `${outlet.name} (${outlet.city})`
    }));

    const addonOptions = availableAddons.map(addon => ({
        value: addon._id,
        label: `${addon.title} ${addon.status === "ACTIVE" ? "✅" : "⏸️"}`
    }));

    const getExcludedValueOptions = () => {
        return currentAddon.addonValues.map(av => ({
            value: av._id,
            label: `${av.sequenceNumber}. ${av.value}`
        }));
    };

    return (
        <div className="allocate-addon-step">
            {/* Add More Modal */}
            <Modal show={showAddMoreModal} onHide={() => setShowAddMoreModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Another Addon?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Addon allocated successfully! Would you like to:</p>
                    <div className="text-center">
                        <p className="mb-3">
                            <strong>Currently allocated:</strong> {allocatedAddons.length} addon(s)
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleAddAnother}>
                        Add Another Addon
                    </Button>
                    <Button variant="success" onClick={handleProceedToNext}>
                        Proceed to Next Step ({allocatedAddons.length} addons)
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Skip Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Skip Add-ons?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to skip adding addons? You can add them later from the product edit page.
                    {allocatedAddons.length > 0 && (
                        <Alert variant="warning" className="mt-3">
                            <strong>Warning:</strong> You have {allocatedAddons.length} addon(s) already allocated. Skipping will discard them.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={confirmSkipAddons}>
                        Yes, Skip Add-ons
                    </Button>
                </Modal.Footer>
            </Modal>

            <h6 className="mb-4 text-primary">Step 3: Allocate Add-ons <span className="text-muted fw-normal">(Optional)</span></h6>

            {!productData._id && (
                <Alert variant="warning" className="mb-4">
                    <Alert.Heading>Product Not Created Yet</Alert.Heading>
                    <p>Please go back to Step 1 and create the product first to get the Product ID.</p>
                </Alert>
            )}

            {/* Allocated Addons Summary */}
            {allocatedAddons.length > 0 && (
                <Card className="mb-4">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="mb-0 fw-semibold">Allocated Addons ({allocatedAddons.length})</h6>
                                <small className="text-muted">Addons already allocated to this product</small>
                            </div>
                            <Badge bg="success" pill>
                                {allocatedAddons.length} Added
                            </Badge>
                        </div>
                        
                        <div className="table-responsive">
                            <Table size="sm" hover>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Addon Name</th>
                                        <th>Values</th>
                                        <th>Excluded Values</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allocatedAddons.map((addon, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <strong>{addon.addonName}</strong>
                                            </td>
                                            <td>
                                                <Badge bg="info">
                                                    {addon.allocatedValues?.length || 0} values
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg="secondary">
                                                    {addon.excludedValues?.length || 0} excluded
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => removeAllocatedAddon(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => updateAddon(index, addon)}
                                                    >
                                                        Update
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Add New Addon Form */}
            <Card className="mb-4 bg-white">
                <Card.Body>
                    <h6 className="mb-3 fw-semibold">
                        {allocatedAddons.length > 0 ? 'Add Another Addon' : 'Add New Addon'}
                    </h6>

                    <Row className="gy-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Add-on Group</Form.Label>
                                <Select
                                    options={addonOptions}
                                    value={currentAddon.selectedAddon}
                                    onChange={handleAddonChange}
                                    placeholder={availableAddons.length > 0 ? "Select addon group..." : "No more addons available"}
                                    isLoading={isLoading}
                                    isDisabled={availableAddons.length === 0}
                                />
                                {availableAddons.length === 0 && (
                                    <Form.Text className="text-success">
                                        All available addons have been allocated. Click "Proceed to Next Step" below.
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Excluded Outlets (Optional)</Form.Label>
                                <Select
                                    isMulti
                                    options={outletOptions}
                                    value={currentAddon.selectedExcludedOutlets}
                                    onChange={handleExcludedOutletChange}
                                    components={animatedComponents}
                                    placeholder="Select outlets to exclude..."
                                    isDisabled={!currentAddon.selectedAddon}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Excluded Add-on Values (Optional)</Form.Label>
                                <Select
                                    isMulti
                                    options={getExcludedValueOptions()}
                                    value={currentAddon.selectedExcludedValues}
                                    onChange={handleExcludedValueChange}
                                    components={animatedComponents}
                                    placeholder={currentAddon.selectedAddon ? "Select values to exclude..." : "Select addon group first"}
                                    isDisabled={!currentAddon.selectedAddon}
                                />
                                <Form.Text className="text-muted">
                                    These values will not be available for this product
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Configure Addon Values Table */}
            {currentAddon.selectedAddon && (
                <Card className="mb-4">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-semibold">Configure Add-on Values</h6>
                            <Badge bg="info">
                                {currentAddon.addonValues.length} values available
                            </Badge>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Loading addon values...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table striped hover>
                                    <thead>
                                        <tr>
                                            <th>Add-on Value</th>
                                            <th>Status</th>
                                            <th>Price (₹)</th>
                                            <th>Sub Label</th>
                                            <th>Default</th>
                                            <th>Exclude Outlets</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentAddon.allocatedValues
                                            .filter(val => !currentAddon.selectedExcludedValues.some(ex => ex.value === val.value.value))
                                            .map((val, index) => (
                                                <tr key={val.value.value}>
                                                    <td>
                                                        <strong>{val.value.label}</strong>
                                                    </td>
                                                    <td>
                                                        <Badge bg={currentAddon.addonValues.find(av => av._id === val.value.value)?.status === "ACTIVE" ? "success" : "secondary"}>
                                                            {currentAddon.addonValues.find(av => av._id === val.value.value)?.status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            value={val.price || ''}
                                                            onChange={(e) => handleValuePriceChange(val.value.value, e.target.value)}
                                                            placeholder="0.00"
                                                            style={{ width: '120px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="text"
                                                            value={val.subLabel || ''}
                                                            onChange={(e) => handleValueSubLabelChange(val.value.value, e.target.value)}
                                                            placeholder="Optional label"
                                                            style={{ width: '150px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Check
                                                            type="radio"
                                                            name="defaultAddonValue"
                                                            checked={val.isDefault || false}
                                                            onChange={() => handleDefaultChange(val.value.value)}
                                                            className="form-check-inline"
                                                        />
                                                    </td>
                                                    <td>
                                                        <Select
                                                            isMulti
                                                            options={outletOptions}
                                                            value={val.excludedOutlets || []}
                                                            onChange={(selected) => handleValueExcludedOutlets(val.value.value, selected)}
                                                            components={animatedComponents}
                                                            placeholder="Exclude outlets..."
                                                            styles={{
                                                                control: (provided) => ({
                                                                    ...provided,
                                                                    minHeight: '38px',
                                                                    fontSize: '14px'
                                                                })
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            <div className="mt-4 d-flex justify-content-between">
                <div>
                    <Button
                        variant="outline-secondary"
                        onClick={onBack}
                        disabled={isSaving || isSubmitting}
                    >
                        ← Back to Menu Pricing
                    </Button>
                    
                    <Button
                        variant="outline-warning"
                        onClick={handleSkipAddons}
                        disabled={isSaving || isSubmitting}
                        className="ms-2"
                    >
                        Skip All Add-ons
                    </Button>
                </div>
                
                <div>
                    {currentAddon.selectedAddon && (
                        <Button
                            variant="primary"
                            onClick={saveCurrentAddon}
                            disabled={isSaving || isSubmitting || !productData._id}
                            className="me-2"
                        >
                            {isSaving ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Saving Addon...
                                </>
                            ) : (
                                'Save This Addon'
                            )}
                        </Button>
                    )}
                    
                    <Button
                        variant="success"
                        onClick={handleProceedToNext}
                        disabled={isSaving || isSubmitting || !productData._id || (allocatedAddons.length === 0 && !currentAddon.selectedAddon)}
                    >
                        {allocatedAddons.length > 0 ? (
                            `Proceed to Next Step (${allocatedAddons.length} addons) →`
                        ) : (
                            'Proceed Without Addons →'
                        )}
                    </Button>
                </div>
            </div>

            {/* Edit Allocation Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                <Form onSubmit={handleEditAllocationSubmit}>
                    <Modal.Header closeButton style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}>
                        <Modal.Title>Edit Addon Allocation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: "1.5rem" }}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className='fw-semibold'>Product</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editFormData?.product?.label || ""}
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className='fw-semibold'>Addon</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editFormData?.addon?.label || ""}
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md="6">
                                <Form.Group>
                                    <Form.Label className='fw-semibold'>Main Excluded Outlets</Form.Label>
                                    <Select
                                        isMulti
                                        options={outletOptions}
                                        value={editFormData.excludedOutlets}
                                        onChange={(selected) => handleEditMainSelectChange(selected, null, true)}
                                        components={animatedComponents}
                                        styles={customStyles}
                                        placeholder="Select outlets..."
                                    />
                                </Form.Group>
                            </Col>
                            
                            <Col>
                                <Form.Group>
                                    <Form.Label className='fw-semibold'>Add Excluded Addon Value</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Select
                                            options={editFormData.excludedValues}
                                            value={selectedExcludedOption}
                                            onChange={(selected) => setSelectedExcludedOption(selected)}
                                            placeholder="Select to add..."
                                            isClearable
                                            className='w-100'
                                        />
                                        <Button
                                            variant="success"
                                            onClick={() => {
                                                if (!selectedExcludedOption) return;

                                                const updatedExcluded = editFormData.excludedValues.filter(
                                                    (opt) => opt.value !== selectedExcludedOption.value
                                                );

                                                const newValueRow = {
                                                    value: selectedExcludedOption,
                                                    price: '',
                                                    subLabel: '',
                                                    excludedOutlets: [],
                                                    isDefault: false,
                                                };

                                                const updatedValues = [...editFormData.values, newValueRow];

                                                setEditFormData({
                                                    ...editFormData,
                                                    excludedValues: updatedExcluded,
                                                    values: updatedValues,
                                                });

                                                setSelectedExcludedOption(null);
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        {editFormData?.excludedValues && editFormData?.excludedValues?.length > 0 && (
                            <Col>
                                <div className="mb-3">
                                    <span className='fw-semibold'>Excluded Values :</span>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
                                        {editFormData?.excludedValues?.map((opt) => (
                                            <Badge
                                                key={opt?.value}
                                                bg="secondary"
                                                className="d-flex align-items-center"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleEditRemoveExcludedValue(opt)}
                                            >
                                                {opt?.label} <TrashFill size={12} className="ms-1" />
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        )}

                        <hr />
                        <h5 className="mb-3">Allocated Values</h5>
                        {editFormData?.values?.map((val, index) => (
                            <React.Fragment key={index}>
                                <Row className="mb-2 align-items-end">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Addon Value</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={val?.value ? val?.value?.label : ""}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Price (₹)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={val?.price}
                                                onChange={(e) => handleEditValueChange(index, "price", e.target.value)}
                                                placeholder="Price"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group>
                                            <Form.Label>Sub Label</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={val?.subLabel}
                                                onChange={(e) => handleEditValueChange(index, "subLabel", e.target.value)}
                                                placeholder="Sub label"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} className="text-center">
                                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                            <Form.Group>
                                                <Form.Check
                                                    type="switch"
                                                    id={`edit-allocated-default-switch-${index}`}
                                                    label="Default"
                                                    checked={val?.isDefault || false}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const updatedValues = editFormData.values.map((row, idx) => ({
                                                                ...row,
                                                                isDefault: idx === index
                                                            }));
                                                            setEditFormData({ ...editFormData, values: updatedValues });
                                                        } else {
                                                            const updatedValues = editFormData.values.map((row, idx) =>
                                                                idx === index ? { ...row, isDefault: false } : row
                                                            );
                                                            setEditFormData({ ...editFormData, values: updatedValues });
                                                        }
                                                    }}
                                                /> 
                                            </Form.Group>
                                            {editFormData?.values?.length > 1 && (
                                                <Button variant="danger" size="sm" onClick={() => handleEditRemoveValue(index)}>
                                                    <TrashFill size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12">
                                        <Form.Group>
                                            <Form.Label>Exclude Outlets</Form.Label>
                                            <Select
                                                isMulti
                                                options={outletOptions}
                                                value={val.excludedOutlets}
                                                onChange={(selected) => handleEditMainSelectChange(selected, index)}
                                                components={animatedComponents}
                                                styles={customStyles}
                                                placeholder="Select outlets..."
                                            />
                                        </Form.Group>
                                    </Col>
                                    <hr className='mt-2' />
                                </Row>
                            </React.Fragment>
                        ))}
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: "none", padding: "0.75rem 1.5rem" }}>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={isEditBtnLoading}>
                            {isEditBtnLoading ? (
                                <>
                                    <Spinner size='sm' animation="border" className="me-2" />
                                    Updating...
                                </>
                            ) : (
                                "Update Allocation"
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default AllocateAddon;